var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useCallback, useEffect, useRef, useState } from "react";
import { toErrorWithResponse } from "../utils/httpErrors";
import { useStableOkapiKy } from "./useStableOkapiKy";
/** Delay between two status polls (after the previous answer arrived). */
export var JOB_POLL_INTERVAL_MS = 2000;
/** Consecutive failed polls after which the job state is reported as unknown (polling goes on). */
var POLL_ERRORS_BEFORE_FAILED = 3;
/** Longest delay between two polls while the status keeps failing. */
var MAX_POLL_BACKOFF_MS = 30000;
/**
 * Delay before the next poll after `consecutiveErrors` failed polls in a row:
 * 2 s, 4 s, 8 s, 16 s, then 30 s.
 */
export var pollBackoffMs = function (consecutiveErrors) {
    return Math.min(JOB_POLL_INTERVAL_MS * Math.pow(2, Math.max(0, consecutiveErrors - 1)), MAX_POLL_BACKOFF_MS);
};
/** Statuses meaning "this job is gone for you" — expired, cleaned up or another user's. */
var FORGOTTEN_JOB_STATUSES = [403, 404];
var readStoredId = function (storageKey) {
    try {
        return sessionStorage.getItem(storageKey);
    }
    catch (_a) {
        return null;
    }
};
var storeId = function (storageKey, id) {
    try {
        if (id) {
            sessionStorage.setItem(storageKey, id);
        }
        else {
            sessionStorage.removeItem(storageKey);
        }
    }
    catch (_a) {
        // storage unavailable (private mode) — the job just is not resumable
    }
};
/**
 * Tracks one asynchronous server job (report, Solr reindex, …) by polling its
 * status (DB-876).
 *
 * After {@link start} the status is polled: the first request goes out at once,
 * every next one {@link JOB_POLL_INTERVAL_MS} ms after the previous answer, so
 * there is never more than one request in flight. Polling stops when the job
 * leaves the active state and on unmount (the in-flight request is aborted).
 * A failing status request is retried with a growing delay (2 s → 30 s);
 * after three failures in a row `pollFailed` is set — the job is kept and
 * {@link retry} polls again at once. The job id is kept in
 * sessionStorage, so a user who leaves the page and comes back sees the job
 * again. A stored job the server answers with 404 / 403 is silently forgotten.
 */
export var usePolledJob = function (_a) {
    var storageKey = _a.storageKey, fetchStatus = _a.fetchStatus, isActive = _a.isActive;
    var ky = useStableOkapiKy();
    var _b = useState(function () {
        return readStoredId(storageKey);
    }), jobId = _b[0], setJobId = _b[1];
    var _c = useState(null), state = _c[0], setState = _c[1];
    var _d = useState(false), pollFailed = _d[0], setPollFailed = _d[1];
    var _e = useState(0), retryCount = _e[0], setRetryCount = _e[1];
    var start = useCallback(function (id, initial) {
        if (initial === void 0) { initial = null; }
        storeId(storageKey, id);
        setPollFailed(false);
        setState({ id: id, value: initial });
        setJobId(id);
    }, [storageKey]);
    var retry = useCallback(function () {
        setPollFailed(false);
        setRetryCount(function (count) { return count + 1; });
    }, []);
    /** Stops remembering the job across page loads; its last state stays shown. */
    var forgetStored = useCallback(function () { return storeId(storageKey, null); }, [storageKey]);
    var reset = useCallback(function () {
        storeId(storageKey, null);
        setPollFailed(false);
        setState(null);
        setJobId(null);
    }, [storageKey]);
    // the callbacks are usually inline functions — keep the effect keyed on the job only
    var callbacksRef = useRef({ fetchStatus: fetchStatus, isActive: isActive });
    callbacksRef.current = { fetchStatus: fetchStatus, isActive: isActive };
    useEffect(function () {
        if (!jobId) {
            return undefined;
        }
        var cancelled = false;
        var timer = null;
        var controller = null;
        var consecutiveErrors = 0;
        var poll = function () { return __awaiter(void 0, void 0, void 0, function () {
            var data, error_1, status_1;
            var _a, _b;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        controller = new AbortController();
                        _c.label = 1;
                    case 1:
                        _c.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, callbacksRef.current.fetchStatus(ky, jobId, controller.signal)];
                    case 2:
                        data = _c.sent();
                        if (cancelled)
                            return [2 /*return*/];
                        consecutiveErrors = 0;
                        setPollFailed(false);
                        setState({ id: jobId, value: data });
                        if (callbacksRef.current.isActive(data)) {
                            timer = setTimeout(poll, JOB_POLL_INTERVAL_MS);
                        }
                        return [3 /*break*/, 4];
                    case 3:
                        error_1 = _c.sent();
                        if (cancelled)
                            return [2 /*return*/];
                        status_1 = (_b = (_a = toErrorWithResponse(error_1)) === null || _a === void 0 ? void 0 : _a.response) === null || _b === void 0 ? void 0 : _b.status;
                        if (status_1 !== undefined && FORGOTTEN_JOB_STATUSES.includes(status_1)) {
                            storeId(storageKey, null);
                            setState(null);
                            setJobId(null);
                            return [2 /*return*/];
                        }
                        consecutiveErrors += 1;
                        if (consecutiveErrors >= POLL_ERRORS_BEFORE_FAILED) {
                            console.error("Error polling job status:", error_1);
                            setPollFailed(true);
                        }
                        timer = setTimeout(poll, pollBackoffMs(consecutiveErrors));
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        }); };
        poll();
        return function () {
            cancelled = true;
            if (timer)
                clearTimeout(timer);
            controller === null || controller === void 0 ? void 0 : controller.abort();
        };
    }, [jobId, ky, storageKey, retryCount]);
    var current = state && state.id === jobId ? state.value : null;
    return {
        ky: ky,
        jobId: jobId,
        /** Last known state of the tracked job (null until the first answer). */
        state: current,
        /** A job exists and has not finished yet (also while its first status is loading). */
        isRunning: !!jobId && (current === null || isActive(current)),
        /** Three or more status polls in a row failed; polling goes on with a longer delay. */
        pollFailed: pollFailed,
        retry: retry,
        forgetStored: forgetStored,
        start: start,
        reset: reset,
    };
};
